import { Button } from '@systemeio/components';
import { CheckIcon, Edit03Icon, XCloseIcon } from '@systemeio/icons';
import { useSaveEmail } from 'modules/profile/user-settings/hooks/use-save-email';
import { UseUserSettingsInterface } from 'modules/profile/user-settings/types/user-settings-interface';
import React, { FC, PropsWithChildren, useEffect, useState } from 'react';
import { FormInputGroup, Loader, toast } from 'shared/components';
import { AdminRolesEnum } from 'shared/enums/user-roles-enum';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { isValidEmail } from 'shared/utils/email-validation';
import { KeyedMutator } from 'swr';
interface UserSettingsEmailProps {
  email?: string;
  mutate: KeyedMutator<UseUserSettingsInterface>;
}
const UserSettingsEmail: FC<PropsWithChildren<UserSettingsEmailProps>> = ({
  email = '',
  mutate,
  children
}) => {
  const {
    t
  } = useLocoTranslation();
  const {
    checkImpersonatorAdminRoles,
    isSubAccountImpersonation
  } = useUser();
  const canManage = !isSubAccountImpersonation && checkImpersonatorAdminRoles([AdminRolesEnum.admin, AdminRolesEnum.superAdmin]);
  const [isEditing, setIsEditing] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [newEmail, setNewEmail] = useState(email);
  const {
    saveEmail
  } = useSaveEmail();
  useEffect(() => {
    setNewEmail(email);
  }, [email]);
  const onSave = async () => {
    try {
      if (!isValidEmail(newEmail)) {
        return toast.error(t('dashboard.validation.email_not_valid'));
      }
      setIsFetching(true);
      await saveEmail(newEmail);
      await mutate(data => {
        if (data && data.user) {
          return {
            ...data,
            user: {
              ...data.user,
              email: newEmail
            }
          };
        }
      }, false);
      toast.success(t('global.changes_saved'));
      setIsEditing(false);
    } catch {} finally {
      setIsFetching(false);
    }
  };
  const isSameEmail = newEmail === email;
  const isEmailEmpty = newEmail === '';
  return <div className="bg-bg-primary border-border-100 border-b p-5">
      {!(isSubAccountImpersonation && !email) && <div className={'text-text-primary flex h-[42px] items-center gap-1.5 text-base font-bold'}>
          <span>{`${t('global.email')}:`}</span>
          {isEditing ? <FormInputGroup className="w-64" value={newEmail} onChange={e => setNewEmail(e.target.value)} disabled={isFetching} statusIcon={isFetching && <Loader small={true} />} /> : <span>{email}</span>}
          {isEditing && <Button variant={'ghost-color'} size={'icon-xs'} onClick={onSave} disabled={isFetching || isSameEmail || isEmailEmpty}>
              <CheckIcon />
            </Button>}
          {canManage && <Button variant={isEditing ? 'destructive-ghost' : 'ghost-color'} size={'icon-xs'} disabled={isFetching} onClick={() => {
        if (isEditing) {
          setNewEmail(email);
        }
        setIsEditing(!isEditing);
      }}>
              {isEditing ? <XCloseIcon /> : <Edit03Icon />}
            </Button>}
        </div>}
      {children}
    </div>;
};
export default UserSettingsEmail;